import React, { useRef, useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import {
  Button,
  Input,
  Form,
  Grid,
  SelectForm
} from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import { useModal, useLoading, useException } from 'hooks'
import { useGet } from 'hooks'
import Draggable from 'react-draggable'
import { useForm } from 'react-hook-form'
import { IEntityGST, IResponseTypes } from 'services/core/genericEntityGST'
import { CadastroSegurancaItensSubItensServices, IGetAllSubItensParams } from 'services/GST/CadastroItensSubitensSeguranca'
import { IGetInfracoesResponse } from 'services/GST/Dicionarios'

import { useSecurityItensAndSubItens } from '../../../../context'
import { SCHEMA_ITEM, SCHEMA_SUBITEM } from '../../../../yup/index'

interface IForm {
  nmItemSeguranca: string
  dsItemSeguranca: string
  txLegislacaoItemSeguranca: string
  nmSubItem: string
  dsSubItem: string
  idInfracao: number
}

interface IModalEditItemSubItemProps {
  refreshItemsSeguranca: (_params: { pageNumber: number }) => void
  refreshSubitensSeguranca: (_params: IGetAllSubItensParams) => void
}

const formNamesSchema = {
  item: ['nmItemSeguranca', 'dsItemSeguranca', 'txLegislacaoItemSeguranca'],
  subItem: ['nmSubItem', 'dsSubItem', 'idInfracao']
}

const ModalEditItemSubItem: React.FC<IModalEditItemSubItemProps> = ({
  refreshItemsSeguranca,
  refreshSubitensSeguranca
}) => {
  const {
    toggleEditItemModal,
    showEditItemModal,
    selectedItemForEdit,
    selectedItem
  } = useSecurityItensAndSubItens()
  const { setLoading } = useLoading()
  const { createCustomException, getException } = useException()
  const { showModal } = useModal()
  const [EDIT_TYPE, setEditType] = useState<'item' | 'subItem'>('item')
  const { data } = useGet<{}, IEntityGST<IGetInfracoesResponse[]>>({
    api: 'Gst',
    condition: EDIT_TYPE !== 'subItem',
    params: {},
    url: 'Infracao/GetInfracaoToDictionary'
  })
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<IForm>({
    resolver: yupResolver(EDIT_TYPE === 'item' ? SCHEMA_ITEM : SCHEMA_SUBITEM)
  })

  const draggableReference = useRef<Draggable>(null)

  const updateItemSeguranca = (form: IForm) => {
    setLoading('loading')

    CadastroSegurancaItensSubItensServices()
      .updateItemSeguranca({
        idItemSeguranca: selectedItemForEdit.idItemSeguranca,
        nmItemSeguranca: form.nmItemSeguranca,
        txDescricaoItemSeguranca: form.dsItemSeguranca,
        txLegislacaoItemSeguranca: form.txLegislacaoItemSeguranca,
      })
      .then(response => {
        if (response.status !== 200) {
          const { tipo, detalhe, mensagem } = response.data

          createCustomException({
            detalhe,
            mensagem,
            tipo: IResponseTypes[tipo],
          })
        }

        showModal({
          hideButtonCancel: true,
          message: 'Item segurança atualizado com sucesso!',
          txtButtonConfirm: 'OK',
          type: 'success',
        })
        refreshItemsSeguranca({ pageNumber: 1 })
        setLoading('idle')
      })
      .catch((err: Error) => {
        setLoading('idle')
        const error = getException(err)

        showModal({
          hideButtonCancel: true,
          message: error.mensagem || 'Não foi possível atualizar o item segurança',
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }

  const updateSubItemSeguranca = (form: IForm) => {
    setLoading('loading')

    CadastroSegurancaItensSubItensServices()
      .updateSubItemSeguranca({
        idInfracaoSubItemSeguranca: form.idInfracao,
        idSubItemSeguranca: selectedItemForEdit['idSubItemSeguranca'],
        itemSegurancaId: selectedItemForEdit.idItemSeguranca,
        nmSubItemSeguranca: form.nmSubItem,
        txSubItemDescricao: form.dsSubItem,
      })
      .then(response => {
        if (response.status !== 200) {
          const { tipo, detalhe, mensagem } = response.data

          createCustomException({
            detalhe,
            mensagem,
            tipo: IResponseTypes[tipo],
          })
        }

        showModal({
          hideButtonCancel: true,
          message: 'SubItem segurança atualizado com sucesso!',
          txtButtonConfirm: 'OK',
          type: 'success',
        })
        refreshSubitensSeguranca({
          idItemSeguranca: selectedItem?.idItemSeguranca,
          pageNumber: 1
        })
        setLoading('idle')
      })
      .catch((err: Error) => {
        setLoading('idle')
        const error = getException(err)

        showModal({
          hideButtonCancel: true,
          message: error.mensagem || 'Não foi possível atualizar o subitem segurança',
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }

  const handleSubmitForm = (form: IForm) => {
    if (EDIT_TYPE === 'item') {
      updateItemSeguranca(form)
    } else {
      updateSubItemSeguranca(form)
    }

    resetModalState(draggableReference)
    toggleEditItemModal()
  }

  const fillInputs = () => {
    Object.keys(selectedItemForEdit).forEach((field: keyof IForm) => {
      const value = selectedItemForEdit[field]

      setValue(field, value)
    })
  }

  useEffect(() => {
    if (selectedItemForEdit) {
      fillInputs()
      if (selectedItemForEdit.isSubItem) {
        setEditType('subItem')
        return
      }

      setEditType('item')
    }
  }, [selectedItemForEdit])

  const handleClearForm = () => {
    formNamesSchema[EDIT_TYPE].forEach((field: keyof IForm) => {
      setValue(field, '')
    })
  }

  return (
    <DraggableDialog
      showModal={showEditItemModal}
      reference={draggableReference}
      onClose={toggleEditItemModal}
      width={'md'}
    >
      <DialogTitle>EDITAR</DialogTitle>
      <Form
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        <DialogContent>
          <Grid gridCols="1fr 1fr 1fr" gridGap="0.5625rem">
            <Input
              control={control}
              errors={errors}
              label="Item"
              placeholder=""
              formValue={formNamesSchema[EDIT_TYPE][0]}
              required
            />
            <Input
              control={control}
              errors={errors}
              label="Descrição do item"
              formValue={formNamesSchema[EDIT_TYPE][1]}
              required
            />
            {EDIT_TYPE === 'item' ? (
              <Input
                control={control}
                errors={errors}
                label="Legislação"
                formValue={formNamesSchema[EDIT_TYPE][2]}
                required
              />
            ) : (
              <SelectForm
                control={control}
                errors={errors}
                listOptions={data?.entity?.map(infracao => ({
                  label: infracao.nuNivelInfracao,
                  value: infracao.idInfracao,
                })) || []}
                label="Infração"
                formValue={formNamesSchema[EDIT_TYPE][2]}
                required
              />
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            text="Limpar"
            variantButton="text"
            onClick={handleClearForm}
          />
          <Button
            variantButton="default"
            text="SALVAR"
            type="submit"
            width={'max-content'}
          />
        </DialogActions>
      </Form>
    </DraggableDialog>
  )
}

export default ModalEditItemSubItem
